/**
 * MarkdownCodeEditor Component
 *
 * Shared CodeMirror 6 based markdown editor with:
 * - Syntax highlighting via @codemirror/lang-markdown
 * - Dark theme matching the app's gray-900 palette
 * - onInsertText callback for cursor-aware text insertion
 * - No roundtrip loss: source stays markdown
 *
 * Used by: ClaudeMdManager, SystemPromptEditor
 *
 * @module components/shared/MarkdownCodeEditor
 */

import { useCallback, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { EditorView } from '@codemirror/view';
import { placeholder as cmPlaceholder } from '@codemirror/view';

// Dark theme matching app's gray-900 palette
const darkTheme = EditorView.theme({
  '&': {
    backgroundColor: '#111827',
    color: '#e5e7eb',
    fontSize: '14px',
    height: '100%',
    position: 'relative',
  },
  '.cm-content': {
    caretColor: '#60a5fa',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    padding: '8px 0',
  },
  '.cm-gutters': {
    backgroundColor: '#111827',
    color: '#6b7280',
    borderRight: '1px solid #374151',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#1f2937',
  },
  '.cm-activeLine': {
    backgroundColor: '#1f293740',
  },
  '&.cm-focused .cm-cursor': {
    borderLeftColor: '#60a5fa',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': {
    backgroundColor: '#1e3a5f !important',
  },
  '.cm-line': {
    paddingLeft: 'calc(8px + 1.5em)',
    paddingRight: '8px',
    textIndent: '-1.5em',
  },
  '&.cm-focused': {
    outline: 'none',
  },
  '.cm-scroller': {
    overflow: 'auto',
    position: 'absolute !important',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  '.cm-placeholder': {
    color: '#6b7280',
    fontStyle: 'italic',
  },
}, { dark: true });

/**
 * MarkdownCodeEditor - CodeMirror 6 markdown editor
 *
 * @param {Object} props
 * @param {string} props.value - Markdown content
 * @param {function} props.onChange - Called with new string value on change
 * @param {string} [props.placeholder] - Placeholder text when empty
 * @param {function} [props.onInsertText] - Callback receiving an insert function: (insertFn) => void
 *   insertFn signature: (text: string) => void - inserts text at cursor position
 * @param {string} [props.height] - Editor height (default: '100%')
 * @param {boolean} [props.readOnly] - Read-only mode
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MarkdownCodeEditor({
  value,
  onChange,
  placeholder = '',
  onInsertText,
  height = '100%',
  readOnly = false,
  className = '',
}) {
  const editorViewRef = useRef(null);

  // Build extensions array
  const extensions = [
    markdown({ base: markdownLanguage, codeLanguages: languages }),
    EditorView.lineWrapping,
    ...(placeholder ? [cmPlaceholder(placeholder)] : []),
    ...(readOnly ? [EditorView.editable.of(false)] : []),
  ];

  // Capture EditorView reference and expose insert function
  const handleCreateEditor = useCallback((view) => {
    editorViewRef.current = view;

    // Expose insert function to parent
    if (onInsertText) {
      const insertFn = (text) => {
        if (!view) return;
        const { from, to } = view.state.selection.main;
        view.dispatch({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
        });
        view.focus();
      };
      onInsertText(insertFn);
    }
  }, [onInsertText]);

  const handleChange = useCallback((val) => {
    onChange(val);
  }, [onChange]);

  return (
    <div className={`h-full overflow-hidden ${className}`}>
      <CodeMirror
        value={value}
        onChange={handleChange}
        theme={darkTheme}
        extensions={extensions}
        height={height}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          foldGutter: true,
          bracketMatching: true,
          indentOnInput: true,
          autocompletion: false,
          searchKeymap: true,
        }}
        onCreateEditor={handleCreateEditor}
      />
    </div>
  );
}
